import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { first, map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MotesPaths, PendingMember } from '../models';
import { DatabaseService } from './database.service';
import { AddMemberBody, InviteMemberBody, ResendInviteBody } from '../api-models/project';
import { RestService } from './rest.service';

@Injectable({ providedIn: 'root' })
export class MemberService {

    constructor(
        private db: DatabaseService,
        private restService: RestService,
        private httpClient: HttpClient) { }

    public watchPendingMembers(projectKey: string): Observable<PendingMember[]> {
        let query = (ref) => ref.orderByChild('projectKey').equalTo(projectKey);

        return this.db.database.list('pendingMembers', query).snapshotChanges().pipe(map(snapList => {
            return snapList.map((snap) => new PendingMember(snap.key, snap.payload.val()));
        }));
    }

    public async addPendingMember(name: string, email: string, projectKey: string, inviterName: string): Promise<number> {
        // Get authenticated user token
        let authOptions = await this.restService.getAuthOptions();
        let body: InviteMemberBody = { name, email, projectKey, inviterName };

        let rsp = await this.httpClient.post<{ status: number }>(MotesPaths.MOTES_REST + "/v1/projects/actions/invite-member", body, {
            headers: authOptions
        }).toPromise().catch((err) => {
            console.log("Could not add pending member: ", err);
            return err;
        });

        return rsp.status;
    }

    public async addProjectMember(pendingMemberKey: string, userKey: string): Promise<number> {
        // Get authenticated user token
        let authOptions = await this.restService.getAuthOptions();
        let body: AddMemberBody = { pendingMemberKey, userKey };

        let rsp = await this.httpClient.post<{ status: number }>(MotesPaths.MOTES_REST + "/v1/projects/actions/add-member", body, {
            headers: authOptions
        }).toPromise().catch((err) => {
            console.log("Could not add member to project: ", err);
            return err;
        });

        return rsp.status;
    }

    public async resendProjectInvitation(pendingMemberKey: string): Promise<number> {
        // Get authenticated user token
        let authOptions = await this.restService.getAuthOptions();
        let body: ResendInviteBody = { pendingMemberKey };

        let rsp = await this.httpClient.post<{ status: number } | void>(MotesPaths.MOTES_REST + "/v1/projects/actions/resend-invite", body, {
            headers: authOptions
        }).toPromise().catch((err) => {
            console.log("Could not resend project invitation: ", err);
            return err;
        });

        return rsp.status;
    }

    public async removePendingMember(pendingMemberKey: string): Promise<boolean> {
        return this.db.database.ref(`pendingMembers/${pendingMemberKey}`).remove().then(() => true).catch((err) => {
            console.log("Could not delete pending member from db: ", err);
            return false;
        });
    }

    public watchPendingMember(pendingMemberKey: string): Observable<PendingMember> {
        return this.db.database.object('pendingMembers/' + pendingMemberKey).snapshotChanges().pipe(map(snap => {
            if(!snap.key || !snap.payload.val()) return null;
            return new PendingMember(snap.key, snap.payload.val());
        }));
    }

    public getPendingMember(pendingMemberKey: string): Promise<PendingMember | null> {
        if(!pendingMemberKey) return null;
        return this.watchPendingMember(pendingMemberKey).pipe(first()).toPromise();
    }


}
